import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { MapPin, Phone, Clock, Facebook, Menu, X, Info, MessageCircle } from "lucide-react";
import { BIZ, HIGHLIGHTS, PRODUCTS, STEPS, REVIEWS } from "./helenData";
import ConceptCTAPill from "@/components/ConceptCTAPill";

const BURGUNDY = "#7A2035";
const GOLD = "#C9A66B";
const INK = "#2E1620";

const fadeUp = { initial: { opacity: 0, y: 22 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true, margin: "-60px" }, transition: { duration: 0.6, ease: "easeOut" } };

function Nav() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const s = () => setScrolled(window.scrollY > 10);
    s(); window.addEventListener("scroll", s);
    return () => window.removeEventListener("scroll", s);
  }, []);
  const links = [["About", "#about"], ["Portfolio", "#portfolio"], ["Reviews", "#reviews"], ["Visit", "#visit"]];
  return (
    <header className={`sticky top-0 z-40 transition-all ${scrolled ? `bg-[${INK}]/95 backdrop-blur-md shadow-lg` : ""}`} style={{ background: INK }}>
      <nav className="max-w-6xl mx-auto px-5 sm:px-8 flex items-center justify-between h-[74px]">
        <a href="#top" data-testid="helen-logo" className="flex items-center gap-3">
          <img src={BIZ.logo} alt={BIZ.name} className="h-11 w-11 rounded-full object-cover ring-1" style={{ borderColor: GOLD }} />
          <span className="helen-serif text-xl text-[#F7ECEF]">Helen</span>
        </a>
        <div className="hidden md:flex items-center gap-8">
          {links.map(([l, h]) => <a key={h} href={h} className="text-sm font-medium text-[#F1D9DF] hover:text-[#C9A66B] transition-colors">{l}</a>)}
          <a href="#visit" data-testid="helen-header-cta" className="rounded-full px-5 py-2 text-sm font-semibold" style={{ background: GOLD, color: INK }}>Consult</a>
        </div>
        <button className="md:hidden text-[#F7ECEF]" onClick={() => setOpen(!open)} data-testid="helen-menu-toggle" aria-label="Menu">{open ? <X size={26} /> : <Menu size={26} />}</button>
      </nav>
      {open && <div className="md:hidden px-5 pb-4 space-y-1" style={{ background: INK }}>{links.map(([l, h]) => <a key={h} href={h} onClick={() => setOpen(false)} className="block py-2.5 text-[#F1D9DF] font-medium">{l}</a>)}</div>}
    </header>
  );
}

export default function HelenSite() {
  return (
    <div id="top" className="helen-root bg-[#FBF4F1] text-[#3A2027] min-h-screen pb-16 md:pb-0" data-testid="helen-concept-root">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@500;600;700&family=Karla:wght@400;500;600&display=swap');
        .helen-root{font-family:'Karla',sans-serif;}
        .helen-serif{font-family:'Playfair Display',serif;}
      `}</style>

      <div data-testid="jewelry-disclaimer" className="bg-[#1F0F17] text-[#D8B7BF] text-center text-xs sm:text-sm px-4 py-2.5">
        <span className="inline-flex items-center gap-2"><Info size={14} style={{ color: GOLD }} className="shrink-0" />Concept website created by <a href="https://mysitecare.eu/ph" target="_blank" rel="noopener noreferrer" className="underline decoration-white/40 hover:decoration-white">MySiteCare</a> — not currently operated by or affiliated with {BIZ.name}.</span>
      </div>
      <Nav />

      {/* HERO — asymmetric editorial split, ring photography leads */}
      <section className="grid md:grid-cols-[1.1fr,1fr] min-h-[78vh]" data-testid="helen-hero">
        <div className="flex flex-col justify-center px-6 sm:px-10 py-16 order-2 md:order-1" style={{ background: INK }}>
          <motion.div {...fadeUp}>
            <span className="text-xs uppercase tracking-[0.3em]" style={{ color: GOLD }}>Bridal &amp; Fine Jewelry · Ongpin</span>
            <h1 className="mt-5 helen-serif text-5xl sm:text-6xl text-[#FBF4F1] leading-[1.05]">Custom rings,<br />made personal.</h1>
            <p className="mt-6 text-[#F1D9DF]/85 max-w-md leading-relaxed">Custom engagement rings, GIA-certified diamonds and bespoke bridal jewelry from a third-generation Ongpin family jeweler.</p>
            <div className="mt-8 flex flex-wrap gap-4">
              <a href="#portfolio" data-testid="helen-hero-cta" className="rounded-full px-7 py-3.5 text-sm font-semibold" style={{ background: GOLD, color: INK }}>Explore Our Collection</a>
              <a href={BIZ.facebook} target="_blank" rel="noopener noreferrer" data-testid="helen-hero-message" className="rounded-full px-7 py-3.5 text-sm font-semibold text-[#F7ECEF] border border-white/30">Message Us</a>
            </div>
            <p className="mt-8 text-sm text-[#D8B7BF]">{BIZ.rating}★ Local Rating · {BIZ.reviewCount} Public Reviews · 3rd-Generation Family Jeweler</p>
          </motion.div>
        </div>
        <motion.div {...fadeUp} className="order-1 md:order-2 relative min-h-[320px]">
          <img src="/helen/hero.jpg" alt="Helen Jewelry Shop bridal rings" className="absolute inset-0 w-full h-full object-cover" style={{ objectPosition: "60% center" }} />
        </motion.div>
      </section>

      {/* ABOUT — narrative + a signature-strengths band (no icon circles) */}
      <section id="about" className="max-w-5xl mx-auto px-5 sm:px-8 py-20">
        <motion.p {...fadeUp} className="text-2xl sm:text-3xl helen-serif leading-relaxed text-center" style={{ color: BURGUNDY }}>
          {BIZ.intro}
        </motion.p>
        <motion.div {...fadeUp} className="mt-14 grid sm:grid-cols-4 divide-y sm:divide-y-0 sm:divide-x" style={{ borderColor: "#EFDCE0" }}>
          {HIGHLIGHTS.map((h) => (
            <div key={h.title} className="px-6 py-6 text-center sm:text-left">
              <p className="helen-serif text-lg" style={{ color: BURGUNDY }}>{h.title}</p>
              <p className="mt-2 text-sm text-[#5A3A42] leading-relaxed">{h.text}</p>
            </div>
          ))}
        </motion.div>
      </section>

      {/* PORTFOLIO — masonry gallery */}
      <section id="portfolio" className="bg-[#F3E3E6]/60 py-20">
        <div className="max-w-6xl mx-auto px-5 sm:px-8">
          <motion.div {...fadeUp} className="text-center max-w-2xl mx-auto mb-10">
            <span className="text-xs font-semibold uppercase tracking-[0.25em]" style={{ color: BURGUNDY }}>Our Jewelry</span>
            <h2 className="mt-2 text-3xl sm:text-4xl helen-serif">Example Pieces &amp; Styles</h2>
            <p className="mt-4 text-[#5A3A42]">Most bridal pieces are made to order. Message us to discuss your design and current pricing.</p>
          </motion.div>
          <div className="grid grid-cols-2 md:grid-cols-4 grid-flow-row-dense auto-rows-[160px] sm:auto-rows-[190px] md:auto-rows-[220px] gap-4">
            {PRODUCTS.map((p) => (
              <a key={p.name} href={BIZ.facebook} target="_blank" rel="noopener noreferrer" data-testid="helen-inquire"
                className={`group relative overflow-hidden shadow-sm hover:shadow-xl transition-all ${p.big ? "col-span-2 row-span-2" : "col-span-1 row-span-1"}`}>
                <img src={p.img} alt={p.name} className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#2E1620]/95 via-[#2E1620]/50 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-4">
                  <h3 className="text-[#FBF4F1] font-semibold text-sm sm:text-base leading-tight">{p.name}</h3>
                </div>
                <span className="absolute top-3 right-3 rounded-full bg-white/90 p-2 opacity-0 group-hover:opacity-100 transition"><MessageCircle size={14} style={{ color: BURGUNDY }} /></span>
              </a>
            ))}
          </div>
          <p className="mt-6 text-center text-sm text-[#5A3A42]">Tap any piece to ask about it directly on Facebook.</p>
        </div>
      </section>

      {/* PROCESS — vertical numbered story, not icon cards */}
      <section className="max-w-3xl mx-auto px-5 sm:px-8 py-20">
        <motion.h2 {...fadeUp} className="text-3xl sm:text-4xl helen-serif text-center mb-14" style={{ color: BURGUNDY }}>From Consultation to Collection</motion.h2>
        <div className="space-y-0">
          {STEPS.map((s, i) => (
            <motion.div key={s.n} {...fadeUp} data-testid={`helen-step-${s.n}`} className="flex gap-6 py-6 border-t" style={{ borderColor: "#EFDCE0" }}>
              <span className="helen-serif text-3xl" style={{ color: GOLD }}>{s.n}</span>
              <div><h3 className="text-lg font-semibold">{s.title}</h3><p className="mt-1 text-sm text-[#5A3A42]">{s.text}</p></div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* REVIEWS */}
      <section id="reviews" className="bg-[#F3E3E6]/60 py-20">
        <div className="max-w-6xl mx-auto px-5 sm:px-8">
          <motion.h2 {...fadeUp} className="text-3xl sm:text-4xl helen-serif text-center mb-14" style={{ color: BURGUNDY }}>What Customers Say</motion.h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {REVIEWS.map((r) => (
              <motion.div key={r.name} {...fadeUp} data-testid="helen-review-card" className="bg-white p-7 shadow-sm">
                <p className="text-[#3A2027] leading-relaxed text-sm">"{r.text}"</p>
                <p className="mt-5 text-xs font-semibold" style={{ color: BURGUNDY }}>{r.name} <a href={r.sourceUrl} target="_blank" rel="noopener noreferrer" className="text-[#9C7A83] font-normal underline underline-offset-2">· {r.source}</a></p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* VISIT */}
      <section id="visit" className="py-20 text-[#F7ECEF]" style={{ background: INK }}>
        <div className="max-w-6xl mx-auto px-5 sm:px-8 grid md:grid-cols-2 gap-12">
          <motion.div {...fadeUp}>
            <h2 className="text-3xl helen-serif">Visit Our Store</h2>
            <ul className="mt-7 space-y-5">
              <li className="flex items-start gap-4"><MapPin size={20} style={{ color: GOLD }} className="mt-0.5" />{BIZ.address}</li>
              <li className="flex items-start gap-4"><Phone size={20} style={{ color: GOLD }} className="mt-0.5" /><a href={`tel:${BIZ.phone.replace(/\s/g, "")}`} className="hover:underline">{BIZ.phone}</a></li>
              <li className="flex items-start gap-4"><Facebook size={20} style={{ color: GOLD }} className="mt-0.5" /><a href={BIZ.facebook} target="_blank" rel="noopener noreferrer" data-testid="helen-facebook-link" className="hover:underline">Message us on Facebook</a></li>
              <li className="flex items-start gap-4"><Clock size={20} style={{ color: GOLD }} className="mt-0.5" />{BIZ.hours}</li>
            </ul>
          </motion.div>
          <motion.div {...fadeUp} className="min-h-[320px]"><iframe title="Helen Jewelry Shop map" src={BIZ.mapEmbed} className="w-full h-full min-h-[320px]" style={{ border: 0 }} loading="lazy" referrerPolicy="no-referrer-when-downgrade" /></motion.div>
        </div>
      </section>

      <footer className="bg-[#1F0F17] text-[#8A6570] py-8">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 flex flex-col sm:flex-row justify-between gap-3 text-xs">
          <p className="helen-serif text-base text-[#F7ECEF]">Helen Jewelry Shop</p>
          <p>This is an unsolicited concept website by MySiteCare. Not operated by or affiliated with {BIZ.name}. Confirm pricing and purity in person.</p>
        </div>
      </footer>

      <a href={BIZ.facebook} target="_blank" rel="noopener noreferrer" data-testid="helen-sticky-contact" className="md:hidden fixed bottom-4 inset-x-4 z-50 rounded-full px-6 py-3.5 text-center text-sm font-semibold shadow-xl flex items-center justify-center gap-2" style={{ background: GOLD, color: INK }}><MessageCircle size={17} /> Message Helen</a>
      <ConceptCTAPill slug="helen-jewelry-shop-concept" />
    </div>
  );
}
